import React, { useState } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import HalfDoughnutChart from './halfDoughnutChart';
import CountUp from 'react-countup';
import { TitlePercentCard } from './styles';
import { StyledCardHeader, StyledCardBody } from '../attentionPoints/styles';
import { IconButton, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../../i18n/useAppLocale';
import { getAnalyticsNumberSeparators } from '../../../i18n/analytics';
interface FieldProps {
  percentage: number | null,
  title: string;
  tooltipText: string;
  filterTag: string | null;
  noDataTooltip?: string;
}

export function getClassificationText(percentage:number | null, labels?: Partial<Record<'critical' | 'bad' | 'fair' | 'good' | 'excellent', string>>) {
  if (percentage === null) {
    return "- -";
  }

  const roundedPercentage = parseFloat(percentage.toFixed(1));

  if (roundedPercentage >= 0 && roundedPercentage < 40) {
    return labels?.critical || "Crítico";
  } else if (roundedPercentage >= 40 && roundedPercentage < 60) {
    return labels?.bad || "Ruim";
  } else if (roundedPercentage >= 60 && roundedPercentage < 70) {
    return labels?.fair || "Razoável";
  } else if (roundedPercentage >= 70 && roundedPercentage < 80) {
    return labels?.good || "Bom";
  } else if (roundedPercentage >= 80 && roundedPercentage <= 100) {
    return labels?.excellent || "Excelente";
  } else {
    return "- -";
  }
}

export function getClassificationColor(percentage: any) {
  if (percentage === null) {
    return "#B9B9B9";
  }

  const roundedPercentage = parseFloat(percentage.toFixed(1));

  if (roundedPercentage >= 0 && roundedPercentage < 40) {
    return "#FD4659";
  } else if (roundedPercentage >= 40 && roundedPercentage < 60) {
    return "#FA7F63";
  } else if (roundedPercentage >= 60 && roundedPercentage < 70) {
    return "#D1AF02";
  } else if (roundedPercentage >= 70 && roundedPercentage < 80) {
    return "#88BF36";
  } else if (roundedPercentage >= 80 && roundedPercentage <= 100) {
    return "#538a00";
  } else {
    return "#B9B9B9";
  }
}

function  getClassificationColorGradient(percentage:any) {
  if (percentage < 60) {
    return "linear-gradient(314deg, #FA7F63 4.99%, #FEE073 100%)";
  } else {
    return "linear-gradient(318deg, #00B2B5 16.44%, #96FFD2 85.22%)"; 
  }

}



const PercentCard: React.FC<FieldProps> = ({ percentage, title, tooltipText, filterTag, noDataTooltip }) => {
  const { t } = useTranslation('insights');
  const appLocale = useAppLocale();
  const numberSeparators = getAnalyticsNumberSeparators(appLocale);
  const classificationLabels = { critical: t('classificationCritical'), bad: t('classificationBad'), fair: t('classificationFair'), good: t('classificationGood'), excellent: t('classificationExcellent') };
  const getNullValueTooltipText = (indicatorTitle: string): string => {
    const lowerCaseTitle = indicatorTitle.toLowerCase();
    if (lowerCaseTitle.includes('ibfoz')) {
      return "É necessário no mínimo 3 respondentes para visualizar esse indicador";
    }

    if (lowerCaseTitle.includes('aderência')) {
      return "Não foi realizada nenhuma reflexão neste mês";
    }

    if (lowerCaseTitle.includes('envolvimento') || lowerCaseTitle.includes('fidelização')) {
      return "Esse indicador engloba 3 meses. Não há o suficiente de respondentes neste mês junto aos dois anteriores para exibir o indicador";
    }
    return "É necessário no mínimo 3 respondentes para visualizar esse indicador";
  };
  
  return (
    <StyledCardBody style={{ color: '#ffff' }}>
      <StyledCardHeader style={{borderRadius: '.7em .7em 0 0 '}}>
        <div style={{display: 'flex', justifyContent: 'center', alignItems:'center'}}>

          <TitlePercentCard>
            {title}{filterTag ? ` (${filterTag})` : ""}
          </TitlePercentCard>

          <div style={{display:'flex', justifyContent: 'flex-end', position: 'absolute', right: '0'}}>
            <Tooltip sx={{fontSize: 80}} enterTouchDelay={0} leaveTouchDelay={2000} title={tooltipText} arrow>
              <IconButton>
                <InfoOutlinedIcon sx={{fontSize: 20, opacity: '.5'}}/>
              </IconButton>
            </Tooltip>
          </div>
         
          

        </div>
        
      </StyledCardHeader>

      <Card.Body style={{background:'#FFF', boxShadow:' 0px 4px 4px 0px rgba(0, 0, 0, 0.05)', borderRadius: '0 0 .7em .7em '}}>


        <Tooltip
          title={noDataTooltip || getNullValueTooltipText(title)}
          arrow
          disableHoverListener={percentage !== null}
        >
          <div className="ms-auto text-center position-relative"
          style={{border:'none'}}
          >
            <div className="d-flex align-items-center justify-content-center" >
              <HalfDoughnutChart
                color={getClassificationColor(percentage)}
                target={100}
                reached={((percentage) ? percentage : 0)}
              />
            </div>
            <div style={{
              position: 'absolute',
              top: '55%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color:  getClassificationColor(percentage),
              fontSize: 28
            }}>
              {percentage !== null ? (
                percentage % 1 !== 0 ? (
                  <CountUp
                    start={0}
                    end={percentage}
                    duration={2.75}
                    suffix="%"
                    decimals={1}
                    separator={numberSeparators.group}
                    decimal={numberSeparators.decimal}
                  />
                ) : (
                  <CountUp
                    start={0}
                    end={percentage}
                    duration={2.75}
                    suffix="%"
                    separator={numberSeparators.group}
                    decimal={numberSeparators.decimal}
                  />
                )
              ) : (
                '--'
              )}
            </div>
            <p style={{ fontSize: 20, color: getClassificationColor(percentage), margin: 0, fontWeight: '600' }}>{getClassificationText(percentage, classificationLabels)}</p>
          </div>
        </Tooltip>


      </Card.Body>
    </StyledCardBody>
  );
};

export default PercentCard;
